import { useCallback, useSyncExternalStore } from 'react';
import { voiceMedia } from '@/lib/voiceMedia';
import { vadThresholdRms } from '@/lib/voicePreferences';
// Same-origin worklet asset (never a Blob/data URL — the CSP blocks those; see
// screenAudioCapture for the outage that taught us).
import micLevelWorkletUrl from '@/lib/voice/micLevelProcessor.js?url';

// VoiceMediaController doesn't export its snapshot type; we infer it from
// the getter so a future schema change at the source flows through here.
type VoiceMediaSnapshot = ReturnType<typeof voiceMedia.getSnapshot>;

/**
 * Per-participant audio-level publisher.
 *
 * Reads the voiceMedia snapshot for `localStream` + every `remoteStream`
 * with `mediaTag === 'microphone'`, wires a Web Audio AnalyserNode to
 * each one, and polls the RMS amplitude on a setInterval timer. The
 * result is a frozen `Map<userId, level: 0..1>` published via
 * `useSyncExternalStore`, so consumers re-render only when something
 * actually changed (and only when subscribed).
 *
 * Deliberately NOT requestAnimationFrame: rAF stops COMPLETELY in a hidden
 * tab, which froze `localSpeaking` and with it the voice-activation mic gate —
 * a user in micMode='voice' stopped transmitting the moment they switched to
 * another tab. A timer merely slows to ~1 Hz in the background, which the
 * SPEAKING_HOLD_MS hysteresis absorbs.
 *
 * A timer is still not good enough for the OWN mic, though: Chromium applies
 * "intensive throttling" after a few minutes hidden (~1 tick per MINUTE), which
 * no hysteresis can absorb — voice activation would sit shut and the user goes
 * silent for everyone. So the local level is measured on the AUDIO THREAD (a
 * tiny AudioWorklet) and pushed via postMessage: audio rendering and message
 * tasks are not throttled by visibility, so «переключил вкладку — меня не
 * слышно» can't come back. Remote tiles keep the timer: their indicator is
 * cosmetic, and their audio plays regardless.
 *
 * Why a separate module:
 *   - Keeps Web Audio plumbing out of voiceMedia, which is already a
 *     mediasoup orchestrator with enough state machine.
 *   - The analysers and AudioContext are torn down automatically when
 *     the last consumer unsubscribes — no rAF loop when nobody listens.
 *   - Local mic and remote streams use the same code path, so the
 *     "you're talking" indicator behaves identically to everyone else's.
 *
 * Why a *one-shot speaking signal* instead of just raw level: the UI
 * cares about a stable boolean ("is this person speaking right now?")
 * rather than the live numeric. We expose the numeric anyway in case
 * a future caller wants to draw a meter; the booleanise step lives at
 * the consumer hook, with hysteresis so a tile doesn't flicker on a
 * single breath.
 */

interface AnalyserEntry {
  // The stream this analyser was built from. Tracked so we can detect when a
  // participant's stream *object* is swapped (mute/unmute, SFU producer
  // re-add, local mic re-acquire) for the same userId and rebuild — otherwise
  // the analyser keeps reading a dead source and the speaking signal sticks.
  stream: MediaStream;
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  // Zero-gain sink wired analyser → sink → destination. A *remote* WebRTC
  // MediaStreamAudioSourceNode produces all-silence in Chrome unless its graph
  // reaches an AudioDestination (a long-standing Chromium quirk; local
  // getUserMedia streams are exempt). The sink gain stays at 0 so this priming
  // path is inaudible — actual playback happens in VoiceAudioLayer's context.
  sink: GainNode;
  // Float time-domain buffer. We deliberately use float (not byte) data:
  // byte silence is 128, but an analyser that's connected yet NOT processing
  // (the Chrome remote-source quirk) fills the byte buffer with 0 → reads as
  // full -1 amplitude → "always speaking". Float silence is 0 either way, so a
  // non-processing/idle analyser reads as silence instead of stuck-on.
  buf: Float32Array<ArrayBuffer>;
}

// RMS amplitude (0..1) at which a REMOTE tile is marked "talking". Aligned with
// the local voice-activation floor (`vadThresholdRms()` ≈ 0.0225 at default
// sensitivity): a remote peer only transmits once their own mic crosses that
// floor, so the listener's "speaking" threshold must sit at/below it — otherwise
// there's a band (≈0.0225–0.07) where the peer is transmitting voice yet their
// tile reads idle. The old 0.07 was ~3× too high and dropped normal/quiet speech.
const SPEAKING_THRESHOLD = 0.025;
const SPEAKING_HOLD_MS = 500; // keep "speaking" true after silence — long enough
// that voice-activation doesn't snap shut between words/syllables.
const FRAME_INTERVAL_MS = 80; // ~12 Hz; humans can't see faster anyway

class VoiceLevelsStore {
  // Snapshot exposed to subscribers. Frozen Map keyed by userId.
  private snapshot: ReadonlyMap<string, number> = new Map();
  // Per-userId hold timer (last time RMS crossed the threshold). Used to
  // compute the booleanised "speaking" with hysteresis.
  private lastAboveAt: Map<string, number> = new Map();
  private speakingSnapshot: ReadonlyMap<string, boolean> = new Map();

  private listeners = new Set<() => void>();
  private speakingListeners = new Set<() => void>();
  private analysersByUserId = new Map<string, AnalyserEntry>();
  // One AudioContext is shared across every analyser. Browsers cap the
  // number of concurrent contexts (typically ~6), so allocating one per
  // participant fails silently in a 7-person room. The shared ctx is
  // created lazily on first start() and closed on full unsubscribe.
  private sharedCtx: AudioContext | null = null;
  private unsubscribeVoice: (() => void) | null = null;
  private timerId: ReturnType<typeof setInterval> | null = null;
  /**
   * Audio-thread meter for the local mic: node + the stream it was built from
   * (so a mic re-acquire rebuilds it) + teardown. `localRms` is the last value
   * pushed from the audio thread; when it's non-null the timer path ignores the
   * local analyser, because the pushed value stays fresh in a hidden tab and
   * the analyser read does not.
   */
  private micMeter: { stream: MediaStream; dispose: () => void } | null = null;
  private localRms: number | null = null;

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    if (this.listeners.size + this.speakingListeners.size === 1) this.start();
    // Block body so the return type is `void`, not `boolean` from Set.delete.
    // useSyncExternalStore's contract is `() => void`; React StrictMode
    // double-invokes the subscribe+cleanup pair and a boolean return throws
    // off its identity comparison, causing re-subscribe/teardown churn.
    return () => {
      this.listeners.delete(cb);
      if (this.listeners.size + this.speakingListeners.size === 0) this.stop();
    };
  };

  subscribeSpeaking = (cb: () => void): (() => void) => {
    this.speakingListeners.add(cb);
    if (this.listeners.size + this.speakingListeners.size === 1) this.start();
    return () => {
      this.speakingListeners.delete(cb);
      if (this.listeners.size + this.speakingListeners.size === 0) this.stop();
    };
  };

  getSnapshot = (): ReadonlyMap<string, number> => this.snapshot;
  getSpeakingSnapshot = (): ReadonlyMap<string, boolean> => this.speakingSnapshot;

  // ---- internals ---------------------------------------------------------

  private start() {
    if (this.unsubscribeVoice) return;
    this.unsubscribeVoice = voiceMedia.subscribe(() => this.rebuildAnalysers());
    this.rebuildAnalysers();
    this.tick();
    this.timerId = setInterval(this.tick, FRAME_INTERVAL_MS);
  }

  /**
   * Recompute + publish the local speaking flag from an audio-thread sample.
   * Runs off a postMessage task, so it keeps firing at full rate while the tab
   * is hidden — this is what actually keeps voice activation alive there.
   */
  private onLocalRms = (rms: number) => {
    this.localRms = rms;
    const now = performance.now();
    const speaking = rms >= vadThresholdRms();
    if (speaking) this.lastAboveAt.set('local', now);
    const held = speaking || now - (this.lastAboveAt.get('local') ?? 0) < SPEAKING_HOLD_MS;
    if (this.speakingSnapshot.get('local') === held) return;
    const next = new Map(this.speakingSnapshot);
    next.set('local', held);
    this.speakingSnapshot = next;
    for (const cb of this.speakingListeners) cb();
  };

  /** Build the audio-thread meter for the local mic. Best-effort: on any
   *  failure we simply keep the analyser+timer path (correct while focused). */
  private async attachMicMeter(ctx: AudioContext, stream: MediaStream): Promise<void> {
    try {
      await ctx.audioWorklet.addModule(micLevelWorkletUrl);
      // The stream may have been swapped while the module loaded.
      if (voiceMedia.getSnapshot().localStream !== stream) return;
      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'mic-level-meter', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      // Zero-gain path to the destination: a worklet whose output goes nowhere
      // is not guaranteed to be pulled by the graph. Gain 0 keeps it inaudible.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      source.connect(node);
      node.connect(sink);
      sink.connect(ctx.destination);
      node.port.onmessage = (e) => this.onLocalRms(Number(e.data) || 0);
      this.micMeter = {
        stream,
        dispose: () => {
          node.port.onmessage = null;
          for (const n of [source, node, sink]) {
            try {
              n.disconnect();
            } catch {
              /* already detached */
            }
          }
        },
      };
    } catch {
      /* no worklet support / module blocked — timer path stays in charge */
    }
  }

  private detachMicMeter() {
    this.micMeter?.dispose();
    this.micMeter = null;
    this.localRms = null;
  }

  private stop() {
    this.unsubscribeVoice?.();
    this.unsubscribeVoice = null;
    if (this.timerId != null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    this.detachMicMeter();
    for (const entry of this.analysersByUserId.values()) {
      disconnectEntry(entry);
    }
    this.analysersByUserId.clear();
    if (this.sharedCtx) {
      try {
        void this.sharedCtx.close();
      } catch {
        /* ignore */
      }
      this.sharedCtx = null;
    }
    this.snapshot = new Map();
    this.lastAboveAt.clear();
    this.speakingSnapshot = new Map();
  }

  /** Sync analyser set against the current snapshot of audio streams. */
  private rebuildAnalysers() {
    const snap: VoiceMediaSnapshot = voiceMedia.getSnapshot();
    // What streams should we be listening to?
    // - localStream (treated as the active user's id; we don't know it
    //   here, so we just key it as 'local'. Consumer maps that to me.id.)
    // - remoteStreams where mediaTag === 'microphone'
    const desired = new Map<string, MediaStream>();
    if (snap.localStream && snap.localStream.getAudioTracks().length > 0) {
      desired.set('local', snap.localStream);
    }
    for (const r of snap.remoteStreams) {
      if (r.mediaTag === 'microphone' && r.stream.getAudioTracks().length > 0) {
        desired.set(r.userId, r.stream);
      }
    }

    // Diff against what we're currently analysing, by stream IDENTITY (not
    // just userId) so a swapped stream for an existing user is rebuilt.
    const current = new Map<string, MediaStream>();
    for (const [userId, entry] of this.analysersByUserId) current.set(userId, entry.stream);
    const { toRemove, toAdd } = diffAnalyserStreams(current, desired);

    // Audio-thread meter follows the local stream identity: a mic re-acquire
    // (device change, unmute) hands us a new stream and the old node would be
    // metering a dead source.
    const localStream = desired.get('local') ?? null;
    if (this.micMeter && this.micMeter.stream !== localStream) this.detachMicMeter();

    for (const userId of toRemove) {
      const entry = this.analysersByUserId.get(userId);
      if (entry) disconnectEntry(entry);
      this.analysersByUserId.delete(userId);
      // Only forget the hold timer when the user is truly gone; a stream swap
      // (still in `desired`) keeps its recent-speech window.
      if (!desired.has(userId)) this.lastAboveAt.delete(userId);
    }

    if (toAdd.length === 0) return;

    // Add new entries — all share the same AudioContext.
    if (!this.sharedCtx) {
      try {
        this.sharedCtx = new AudioContext();
      } catch {
        // No AudioContext available (e.g. SSR or hardened browsers) —
        // leave the analyser map empty; speaking signal stays false.
        return;
      }
    }
    const ctx = this.sharedCtx;
    if (!ctx) return;
    // Now that the graph reaches a destination, a suspended context would
    // stop processing entirely. Resume it (joining voice is a user gesture).
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    for (const userId of toAdd) {
      const stream = desired.get(userId);
      if (!stream) continue;
      try {
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.6;
        // analyser → zero-gain sink → destination so remote streams flow in
        // Chrome (see AnalyserEntry.sink). Gain 0 keeps it silent.
        const sink = ctx.createGain();
        sink.gain.value = 0;
        source.connect(analyser);
        analyser.connect(sink);
        sink.connect(ctx.destination);
        const buf = new Float32Array(
          new ArrayBuffer(analyser.fftSize * Float32Array.BYTES_PER_ELEMENT),
        ) as Float32Array<ArrayBuffer>;
        this.analysersByUserId.set(userId, { stream, source, analyser, sink, buf });
      } catch {
        // getUserMedia tracks can't always feed AudioContext (e.g. screen
        // share with no audio). Quietly skip — we'll just say "not speaking".
      }
    }

    if (localStream && !this.micMeter) {
      void this.attachMicMeter(ctx, localStream);
    }
  }

  private tick = () => {
    if (this.listeners.size + this.speakingListeners.size === 0) return;
    const now = performance.now();
    {
      const nextLevels = new Map<string, number>();
      const nextSpeaking = new Map<string, boolean>();
      for (const [userId, entry] of this.analysersByUserId) {
        // Time-domain analysis for RMS. Frequency-domain (getByteFrequencyData)
        // also works but the math costs the same and time-domain is more
        // direct for "loudness".
        entry.analyser.getFloatTimeDomainData(entry.buf);
        let sumSq = 0;
        const len = entry.buf.length;
        for (let i = 0; i < len; i += 1) {
          const v = entry.buf[i] ?? 0; // [-1, 1]; silence ≈ 0
          sumSq += v * v;
        }
        // The local mic prefers the audio-thread sample: in a hidden tab this
        // analyser read is stale (the timer barely runs) while the pushed value
        // is current.
        const rms =
          userId === 'local' && this.localRms != null ? this.localRms : Math.sqrt(sumSq / len);
        nextLevels.set(userId, rms);
        // Own mic ('local') uses the user's voice-activation sensitivity so the
        // speaking indicator matches when the mic actually opens; remote tiles
        // keep the fixed threshold.
        const threshold = userId === 'local' ? vadThresholdRms() : SPEAKING_THRESHOLD;
        if (rms >= threshold) {
          this.lastAboveAt.set(userId, now);
          nextSpeaking.set(userId, true);
        } else {
          const last = this.lastAboveAt.get(userId) ?? 0;
          nextSpeaking.set(userId, now - last < SPEAKING_HOLD_MS);
        }
      }
      // Only publish if something actually changed (cheap memo).
      if (!mapShallowEqual(this.snapshot, nextLevels, 0.02)) {
        this.snapshot = nextLevels;
        for (const cb of this.listeners) cb();
      }
      if (!boolMapEqual(this.speakingSnapshot, nextSpeaking)) {
        this.speakingSnapshot = nextSpeaking;
        for (const cb of this.speakingListeners) cb();
      }
    }
  };
}

/** Generic key-by-key map comparator. Use `eq` to plug a custom value
 *  comparison (defaults to `Object.is` so primitives just work). */
function mapEqual<K, V>(
  a: ReadonlyMap<K, V>,
  b: ReadonlyMap<K, V>,
  eq: (x: V, y: V) => boolean = Object.is,
): boolean {
  if (a.size !== b.size) return false;
  for (const [k, v] of a) {
    if (!b.has(k)) return false;
    if (!eq(v, b.get(k) as V)) return false;
  }
  return true;
}

// Tiny adapters: the analyser callsites read more naturally with the
// epsilon / boolean intent named, even though both are one-liners now.
const mapShallowEqual = (
  a: ReadonlyMap<string, number>,
  b: ReadonlyMap<string, number>,
  epsilon: number,
) => mapEqual(a, b, (x, y) => Math.abs(x - y) <= epsilon);
const boolMapEqual = (
  a: ReadonlyMap<string, boolean>,
  b: ReadonlyMap<string, boolean>,
) => mapEqual(a, b);

/**
 * Diff the currently-analysed streams against the desired set, keyed by userId
 * but comparing stream IDENTITY. A userId whose stream object changed lands in
 * BOTH `toRemove` and `toAdd` so the caller tears the stale analyser down and
 * rebuilds it against the live stream — fixing the stuck-speaking bug when a
 * participant's stream is swapped (mute/unmute, SFU producer re-add, local mic
 * re-acquire). Pure for testability.
 */
export function diffAnalyserStreams(
  current: ReadonlyMap<string, MediaStream>,
  desired: ReadonlyMap<string, MediaStream>,
): { toRemove: string[]; toAdd: string[] } {
  const toRemove: string[] = [];
  const toAdd: string[] = [];
  for (const [userId, stream] of current) {
    if (!desired.has(userId) || desired.get(userId) !== stream) toRemove.push(userId);
  }
  for (const [userId, stream] of desired) {
    if (!current.has(userId) || current.get(userId) !== stream) toAdd.push(userId);
  }
  return { toRemove, toAdd };
}

/** Tear down an analyser entry's whole graph (source → analyser → sink). */
function disconnectEntry(entry: AnalyserEntry) {
  for (const node of [entry.source, entry.analyser, entry.sink]) {
    try {
      node.disconnect();
    } catch {
      /* already detached */
    }
  }
}

const STORE = new VoiceLevelsStore();

/**
 * Subscribe to the per-userId speaking boolean map. The map is empty
 * when nobody is in a voice channel; otherwise keys are userIds (plus
 * the synthetic 'local' for the caller's own mic — consumers should map
 * it to their own id).
 */
export function useSpeakingMap(): ReadonlyMap<string, boolean> {
  return useSyncExternalStore(
    STORE.subscribeSpeaking,
    STORE.getSpeakingSnapshot,
    STORE.getSpeakingSnapshot,
  );
}

/**
 * Is ONE participant speaking? (`'local'` for yourself.)
 *
 * Prefer this over {@link useSpeakingMap} in anything rendered per
 * participant. The map is a fresh object on every publish, so a component
 * reading it re-renders ~12×/s while ANYONE talks — in the voice grid that
 * meant re-running per-tile permission checks and rebuilding every tile's JSX
 * on each tick (measurable CPU/fan load in a busy room). This returns a
 * primitive, so React bails out unless THIS participant's state flipped.
 */
export function useIsSpeaking(userId: string): boolean {
  const getSnapshot = useCallback(
    () => STORE.getSpeakingSnapshot().get(userId) ?? false,
    [userId],
  );
  return useSyncExternalStore(STORE.subscribeSpeaking, getSnapshot, getSnapshot);
}

/** Raw RMS levels — exposed for future meter visualisations. */
export function useVoiceLevels(): ReadonlyMap<string, number> {
  return useSyncExternalStore(STORE.subscribe, STORE.getSnapshot, STORE.getSnapshot);
}
